import React, { useState, useEffect, useContext } from 'react';
import { Typography, Table, Button, Input, Switch, message, Modal, Form, Tag, Divider } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';
import AddEmployeeBadgeModal from '../components/AddEmployeeBadgeModal';

// Admin > Badges: the badges that can be given to employees. Route and menu
// limit this page to admin and qa_admin, as does the API.
export default function BadgesAdminPage() {
  const currentUser = useContext(UserContext);
  const canAssign   = (currentUser?.CanAssignBadge ?? 0) >= 1;
  const [badges,    setBadges]    = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editBadge, setEditBadge] = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/badges');
      setBadges(res.data);
    } catch {
      message.error('Failed to load badges.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ── Badges by employee ─────────────────────────────────────────────────────
  const [assigned,        setAssigned]        = useState([]);
  const [assignedLoading, setAssignedLoading] = useState(false);
  const [assignOpen,      setAssignOpen]      = useState(false);
  const [employees,       setEmployees]       = useState([]);

  const loadAssigned = async () => {
    setAssignedLoading(true);
    try {
      const res = await api.get('/api/user-badges');
      setAssigned(res.data);
    } catch {
      message.error('Failed to load employee badges.');
    } finally {
      setAssignedLoading(false);
    }
  };

  useEffect(() => { loadAssigned(); }, []);

  const openAssign = () => {
    setAssignOpen(true);
    if (!employees.length) {
      api.get('/api/badges/employees').then((r) => setEmployees(r.data)).catch(() => {});
    }
  };

  const today = dayjs().format('YYYY-MM-DD');
  const canViewSummary = (currentUser?.CanViewAgentSummary ?? 0) >= 1;
  const assignedColumns = [
    { title: 'Employee', dataIndex: 'EmployeeName', key: 'EmployeeName', width: 220,
      sorter: (a, b) => (a.EmployeeName || '').localeCompare(b.EmployeeName || ''),
      render: (v, r) => (
        <>
          {canViewSummary ? <Link to={`/agents?user_id=${r.UserID}`}>{v}</Link> : v}
          {!r.EmployeeIsActive && <Tag style={{ marginLeft: 6 }}>inactive</Tag>}
        </>
      ) },
    { title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 160,
      sorter: (a, b) => (a.TeamName || '').localeCompare(b.TeamName || ''),
      render: (v) => v || '—' },
    { title: 'Badge', dataIndex: 'BadgeName', key: 'BadgeName',
      sorter: (a, b) => (a.BadgeName || '').localeCompare(b.BadgeName || '') },
    { title: 'Year-Month', dataIndex: 'YearMonth', key: 'YearMonth', width: 110,
      sorter: (a, b) => (a.YearMonth || '').localeCompare(b.YearMonth || ''),
      render: (v) => v || '—' },
    { title: 'Expires On', dataIndex: 'ExpiresOn', key: 'ExpiresOn', width: 150,
      sorter: (a, b) => String(a.ExpiresOn || '').localeCompare(String(b.ExpiresOn || '')),
      render: (v) => !v ? '—'
        : <>{String(v).slice(0, 10)}{String(v).slice(0, 10) < today && <Tag color="red" style={{ marginLeft: 6 }}>expired</Tag>}</> },
    { title: 'Special Note', dataIndex: 'SpecialNote', key: 'SpecialNote', width: 200,
      render: (v) => v || '—' },
    { title: 'Assigned By', dataIndex: 'AssignedByName', key: 'AssignedByName', width: 160,
      render: (v) => v || '—' },
  ];

  const openCreate = () => {
    setEditBadge(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setModalOpen(true);
  };

  const openEdit = (badge) => {
    setEditBadge(badge);
    form.setFieldsValue({ name: badge.Name, description: badge.Description || '', is_active: !!badge.IsActive });
    setModalOpen(true);
  };

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const payload = { name: values.name, description: values.description || null, is_active: values.is_active };
      if (editBadge) {
        await api.put(`/api/badges/${editBadge.ID}`, payload);
        message.success('Badge updated.');
      } else {
        await api.post('/api/badges', payload);
        message.success('Badge created.');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (badge) => {
    try {
      await api.put(`/api/badges/${badge.ID}`, { is_active: !badge.IsActive });
      setBadges((prev) => prev.map((b) => b.ID === badge.ID ? { ...b, IsActive: badge.IsActive ? 0 : 1 } : b));
    } catch {
      message.error('Failed to update.');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'ID', key: 'ID', width: 60 },
    { title: 'Name', dataIndex: 'Name', key: 'Name', width: 260,
      sorter: (a, b) => a.Name.localeCompare(b.Name) },
    { title: 'Description', dataIndex: 'Description', key: 'Description',
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
    {
      title: 'Active', dataIndex: 'IsActive', key: 'IsActive', width: 90,
      render: (v, record) => <Switch checked={!!v} size="small" onChange={() => toggleActive(record)} />,
    },
    {
      title: '', key: 'actions', width: 70, align: 'right',
      render: (_, record) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>Edit</Button>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Badges</Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Badge</Button>
      </div>

      <Table
        rowKey="ID"
        dataSource={badges}
        columns={columns}
        loading={loading}
        size="small"
        pagination={false}
        locale={{ emptyText: 'No badges configured.' }}
      />

      <Divider />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Badges by Employee</Typography.Title>
        {canAssign && <Button type="primary" icon={<PlusOutlined />} onClick={openAssign}>Add Employee Badge</Button>}
      </div>

      <Table
        rowKey="ID"
        dataSource={assigned}
        columns={assignedColumns}
        loading={assignedLoading}
        size="small"
        pagination={{ defaultPageSize: 50, showSizeChanger: true }}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'No badges given yet.' }}
      />

      <AddEmployeeBadgeModal
        open={assignOpen}
        employees={employees}
        onClose={() => setAssignOpen(false)}
        onAdded={loadAssigned}
      />

      <Modal maskClosable={false}
        title={editBadge ? 'Edit Badge' : 'New Badge'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText="Save"
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Required' }]}>
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
          <Form.Item label="Active" name="is_active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
